import { styled } from '@linaria/react';
import { Badge } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import type { FloraPage } from '../../../../api/flora-pages-client/flora-pages-client.type';
import { FloraPageVisibility } from '../../../../api/flora-pages-client/flora-pages-client.type';
import { Table, type TableColumn } from '../../../../components/table/table';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DeleteConfirmationModal } from '../../../data-management/components/delete-confirmation-modal';
import { FloraPageActionsMenu } from '../flora-page-actions-menu';
import { useFloraPageActions } from '../use-flora-page-actions';

type Props = {
  pages: FloraPage[];
};

/**
 * List-view of the Pages gallery built on the shared sortable {@link Table}.
 * Columns: Title, Visibility, Modified, and per-row actions. Defaults to
 * most-recently-modified first; rows are clickable to open the viewer.
 * Owner-only actions reuse the same {@link FloraPageActionsMenu} and mutation
 * logic as the grid view via {@link useFloraPageActions}.
 *
 * @param {Props} props - The pages to render.
 * @returns {JSX.Element} The pages table.
 */
export function FloraPagesTable({ pages }: Props) {
  const navigate = useNavigate();
  const {
    isOwner,
    toggleVisibility,
    pageToDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
    isDeleting,
    deleteError,
  } = useFloraPageActions();

  const columns: TableColumn<FloraPage>[] = [
    {
      key: 'title',
      label: 'Title',
      width: '320px',
      sortValue: (row) => row.title.toLowerCase(),
      render: (_value, row) => (
        <TitleLink
          to={`/application/flora-agent/pages/${row.id}`}
          title={row.title}
          onClick={(event) => event.stopPropagation()}
        >
          <span aria-hidden="true">
            <Icon name="article" size={20} />
          </span>
          <TitleText weight="medium">{row.title}</TitleText>
        </TitleLink>
      ),
    },
    {
      key: 'visibility',
      label: 'Visibility',
      sortValue: (row) => row.visibility,
      render: (_value, row) => {
        const isShared = row.visibility === FloraPageVisibility.Organization;
        return (
          <Badge variant="light" color={isShared ? 'green' : 'gray'}>
            {isShared ? 'Organization' : 'Private'}
          </Badge>
        );
      },
    },
    {
      key: 'metadata_updated_at',
      label: 'Modified',
      sortValue: (row) => new Date(row.metadata_updated_at).getTime(),
      render: (_value, row) => (
        <Text size="small" color="var(--ink-lighter)">
          {new Date(row.metadata_updated_at).toLocaleDateString()}
        </Text>
      ),
    },
    {
      key: 'id',
      label: '',
      sortable: false,
      width: '56px',
      render: (_value, row) =>
        isOwner(row) ? (
          // Stop propagation so the kebab menu doesn't also fire the row's navigate click.
          <ActionsCell onClick={(event) => event.stopPropagation()}>
            <FloraPageActionsMenu page={row} onToggleVisibility={toggleVisibility} onRequestDelete={requestDelete} />
          </ActionsCell>
        ) : null,
    },
  ];

  return (
    <>
      <Table<FloraPage>
        data={pages}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate(`/application/flora-agent/pages/${row.id}`)}
        defaultSortedBy="metadata_updated_at"
        defaultSortedByDirection="desc"
        pageSize={Math.max(pages.length, 1)}
        totalItems={pages.length}
        stickyHeader
      />
      <DeleteConfirmationModal
        isOpen={!!pageToDelete}
        onClose={cancelDelete}
        onDelete={confirmDelete}
        title="Delete Page"
        itemName={pageToDelete ? pageToDelete.title : ''}
        isDeleting={isDeleting}
        error={deleteError}
      />
    </>
  );
}

const TitleLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const TitleText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
